// src/pages/common/AddArtist/EditArtist.jsx
import { useEffect, useState } from "react";
import CommonLayout from "../../../layouts/CommonLayout";
import { useParams, useNavigate } from "react-router-dom";
import { allAPiServicesCall } from "../../../services/apiServices";
import { getAuthConfig } from "../../../services/apiUtils";
import slugify from "slugify";
import { toast } from "react-toastify";
import MultiGenreSelect from "../../../components/common/MultiGenreSelect";
import { Spinner } from "react-bootstrap";
import {
  FaUser,
  FaEdit,
  FaMusic,
  FaLink,
  FaImage,
  FaSave,
  FaArrowLeft,
  FaFileAlt,
  FaUpload
} from "react-icons/fa";
import "./EditArtist.css";

export default function EditArtist() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    artist_name: "",
    genres: "",
    artist_description: "",
    priority: 1,
    slug: "",
    artist_image_link: "",
  });
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [genres, setGenres] = useState([]);
  const [selectedGenres, setSelectedGenres] = useState([]);

  useEffect(() => {
    const fetchGenres = async () => {
      try {
        const response = await allAPiServicesCall.getGenres({}, getAuthConfig());
        setGenres(response.data);
      } catch (error) {
        console.error('Error fetching genres:', error);
      }
    };
    fetchGenres();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const res = await allAPiServicesCall.getArtistById(id, getAuthConfig());
        const a = res.data;
        const genreValue = Array.isArray(a.genres) ? a.genres.join(", ") : (a.genres || "");
        setForm({
          artist_name: a.artist_name || "",
          genres: genreValue,
          artist_description: a.artist_description || "",
          priority: a.priority ?? 1,
          slug: a.slug || "",
          artist_image_link: a.artist_image_link || "",
        });
        const genresArray = genreValue ? genreValue.split(',').map(g => g.trim()).filter(Boolean) : [];
        setSelectedGenres(genresArray);
      } catch (e) {
        toast.error("Failed to load artist");
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const onNameBlur = () => {
    if (!form.slug && form.artist_name) {
      setForm((f) => ({
        ...f,
        slug: slugify(f.artist_name, { lower: true, strict: true }),
      }));
    }
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files?.[0] || null;
    setFile(selectedFile);
    if (selectedFile) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result);
      };
      reader.readAsDataURL(selectedFile);
    } else {
      setPreview(null);
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      const fd = new FormData();
      fd.append("artist_name", form.artist_name.trim());
      fd.append("slug", form.slug || slugify(form.artist_name, { lower: true, strict: true }));
      if (selectedGenres.length > 0) fd.append("genres", selectedGenres.join(", "));
      // Always send artist_description, even if empty, to allow clearing it
      fd.append("artist_description", form.artist_description || "");
      if (file) fd.append("image", file);

      const res = await allAPiServicesCall.updateArtist(id, fd, getAuthConfig());
      toast.success(res.message || "Artist updated");
      navigate("/artists");
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <CommonLayout>
        <div className="edit-artist-loading">
          <div className="edit-artist-loading-spinner">
            <Spinner animation="border" />
          </div>
          <p>Loading artist details...</p>
        </div>
      </CommonLayout>
    );
  }

  return (
    <CommonLayout>
      <div className="edit-artist-page">
        {/* Back Button */}
        <button className="edit-artist-back" onClick={() => navigate(-1)}>
          <FaArrowLeft /> Back
        </button>

        {/* Header */}
        <div className="edit-artist-header">
          <div className="edit-artist-header-icon">
            <FaEdit />
          </div>
          <div className="edit-artist-header-text">
            <h1 className="edit-artist-title">Edit Artist</h1>
            <p className="edit-artist-subtitle">Update artist information and details</p>
          </div>
        </div>

        {/* Form Container */}
        <div className="edit-artist-container">
          <form onSubmit={onSubmit} className="edit-artist-form">
            {/* Image Section */}
            <div className="edit-artist-image-section">
              <div className="edit-artist-current-image">
                {preview || form.artist_image_link ? (
                  <img
                    src={preview || form.artist_image_link}
                    alt={form.artist_name}
                    className="edit-artist-avatar"
                  />
                ) : (
                  <div className="edit-artist-avatar-placeholder">
                    <FaUser />
                  </div>
                )}
                <div className="edit-artist-image-badge">
                  <FaImage />
                </div>
              </div>
              <div className="edit-artist-image-info">
                <h3 className="edit-artist-image-title">{form.artist_name || "Artist Name"}</h3>
                <p className="edit-artist-image-hint">Upload a new image to replace the current one</p>
                <label className="edit-artist-upload-btn">
                  <FaUpload /> Choose Image
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    hidden
                  />
                </label>
                {file && (
                  <span className="edit-artist-file-name">{file.name}</span>
                )}
              </div>
            </div>

            {/* Form Fields */}
            <div className="edit-artist-fields">
              {/* Artist Name */}
              <div className="edit-artist-field">
                <label className="edit-artist-label">
                  <FaUser className="edit-artist-label-icon" />
                  Artist Name
                </label>
                <input
                  type="text"
                  className="edit-artist-input"
                  name="artist_name"
                  value={form.artist_name}
                  onChange={onChange}
                  onBlur={onNameBlur}
                  placeholder="Enter artist name"
                  required
                />
              </div>

              {/* Genres */}
              <div className="edit-artist-field">
                <label className="edit-artist-label">
                  <FaMusic className="edit-artist-label-icon" />
                  Genres
                  <span className="edit-artist-label-hint">(select multiple)</span>
                </label>
                <div className="edit-artist-select-wrapper">
                  <MultiGenreSelect
                    genres={genres}
                    selectedGenres={selectedGenres}
                    onChange={setSelectedGenres}
                    placeholder="Select Genres"
                    size="md"
                  />
                </div>
              </div>

              {/* Slug */}
              <div className="edit-artist-field">
                <label className="edit-artist-label">
                  <FaLink className="edit-artist-label-icon" />
                  Slug
                  <span className="edit-artist-label-hint">(URL-friendly name)</span>
                </label>
                <input
                  type="text"
                  className="edit-artist-input"
                  name="slug"
                  value={form.slug}
                  onChange={onChange}
                  placeholder="auto-generated if left empty"
                />
              </div>

              {/* Description */}
              <div className="edit-artist-field edit-artist-field-full">
                <label className="edit-artist-label">
                  <FaFileAlt className="edit-artist-label-icon" />
                  Description
                </label>
                <textarea
                  className="edit-artist-textarea"
                  name="artist_description"
                  value={form.artist_description}
                  onChange={onChange}
                  placeholder="Write a bio or description for this artist..."
                  rows={5}
                />
              </div>
            </div>

            {/* Actions */}
            <div className="edit-artist-actions">
              <button
                type="button"
                className="edit-artist-btn edit-artist-btn-secondary"
                onClick={() => navigate(-1)}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="edit-artist-btn edit-artist-btn-primary"
                disabled={saving}
              >
                {saving ? (
                  <>
                    <Spinner size="sm" animation="border" />
                    Saving...
                  </>
                ) : (
                  <>
                    <FaSave /> Save Changes
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </CommonLayout>
  );
}
